import { useContext, useCallback } from 'react';
import { CardContent, Grid, Typography, Box, Tooltip, Dialog, DialogTitle, DialogContent, Alert, DialogActions } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { LoadingButton } from '@mui/lab';
import CustomDataGrid from '../../../../../reusable-components/datagrid/CustomDataGrid';
import { fCurrency } from '../../../../../utils/formatNumber';
import { CHECKOUT_SINGLE_CART } from '../../../../../reusable-components/datagrid/cartColumns';
import { CartsContext } from './_CartsContext';
import CartCheckoutToolbar from './CartCheckoutToolbar';
import { AwaitButton } from '../../../../../reusable-components/await-button';

export default function CartCheckout()
{
  const { checkoutCartItems, submitCart, submittingCart, personFor, checkoutCartCategory, carts } = useContext(CartsContext);

  const checkoutTableToolbar = useCallback(() => <CartCheckoutToolbar />, []);

  const oneWeekAgo = new Date();
  oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

  return <Grid container direction="row" justifyContent="space-between" alignItems="flex-start">
    <Grid item xs={9.5}>
      <CustomDataGrid
        dontGetRowHeight
        data={checkoutCartItems}
        loading={checkoutCartItems === null}
        gridColumns={CHECKOUT_SINGLE_CART}
        disableSelectionOnClick
        getEstimatedRowHeight={() => 44}
        disableRowSelectionOnClick
        disableColumnMenu
        disableColumnFilter
        disableColumnSelector
        disableColumnReorder
        CustomLeftToolbar={checkoutTableToolbar}
        pinnedColumns={{
          left: ['picture'],
          right: ['vendor', 'total', 'quantity', 'actions'],
        }}
        sx={{
          '& .MuiDataGrid-cell': {
            cursor: 'default !important',
          },
        }}
      />
    </Grid>
    <Grid item xs={2.5}>
      <CardContent>
        {checkoutCartCategory?.categoryId < 0 && <Box sx={{ my: 1 }}>
          {carts.filter(cart => cart.orderable && cart.items?.length > 0).map((cart, index) => (
            <Typography variant="body2" key={index}>
              {cart.category} {cart.items.length} items (
              {fCurrency(cart.items.reduce((acc, item) => acc + item.price * item.quantity, 0))})
            </Typography>
          ))}
        </Box>}
        <Typography variant="body1">
          Total ({checkoutCartItems.length} item{checkoutCartItems.length !== 1 ? 's' : ''}):{' '}
          <Typography variant="body1" component="span" fontWeight="bold">
            {fCurrency(checkoutCartItems.reduce((acc, item) => acc + item.price * item.quantity, 0))}
          </Typography>
        </Typography>
        <Tooltip title={checkoutCartItems?.some(item => new Date(item.dateAdded) < oneWeekAgo && item.itemId === -1) ?
          "Some items were added more than a week ago" : ""} arrow>
          <span>
            <AwaitButton
              variant="contained"
              size="small"
              fullWidth
              onClick={submitCart}
              loading={submittingCart}
              disabled={!personFor || checkoutCartItems?.length === 0}
            >
              Submit Order
            </AwaitButton>
          </span>
        </Tooltip>
      </CardContent>
    </Grid>
    {checkoutCartItems !== null && checkoutCartItems.length === 0 && <CartCheckoutEmptyError />}
  </Grid>;
}

function CartCheckoutEmptyError() {
  const navigate = useNavigate();
  const { facilityId } = useContext(CartsContext);

  return <Dialog fullWidth maxWidth="xs" open onClose={(event, reason) => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
    navigate(`/dashboard/orders/new-order/cart/${facilityId}`);
  }}>
    <DialogTitle sx={{ pb: 2 }}>Cart error</DialogTitle>
    <DialogContent sx={{ typography: 'body2' }}>
      <Alert severity="error" sx={{ mt: 1 }}>This cart is empty.</Alert>
    </DialogContent>
    <DialogActions>
      <LoadingButton variant="outlined" color="inherit" onClick={() => navigate(`/dashboard/orders/new-order/cart/${facilityId}`)}>
        Go back to main cart
      </LoadingButton>
    </DialogActions>
  </Dialog>;
}